import { Link, useRouterState } from "@tanstack/react-router";
import { useAuth } from "contexts/AuthContext";
import { HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useLayout } from "@/contexts/LayoutContext";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { HELP_PAGE_OFFERED } from "@/components/help/helpAvailability";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { UserDropdown } from "@/components/layout/UserDropdown";
import { WaitingBadge } from "@/components/layout/WaitingBadge";
import { useEndUserDestinations } from "@/components/layout/endUserDestinations";
import hbkLogo from "@/assets/hbk-logo.png";

/**
 * Top navbar. The nav items are the
 * end-user shell — they render only in the end-user area (admin areas keep
 * their sidebars); the logo, help button, language switch, bell and
 * user dropdown (the portal switcher) are global chrome.
 *
 * Which destinations, in what order, and what is waiting on the reader come
 * from `endUserDestinations` — shared with the dashboard's action cards, which
 * offered a different subset while the two lists were written out separately
 * (Jira NAV-90).
 */
export function Navbar() {
  const { isLoggedIn } = useAuth();
  const { features } = useFeatureFlags();
  const { setNavbarHeightPx, bannerStackHeightPx } = useLayout();
  const { t } = useTranslation(["navigation", "common"]);
  const navbarRef = useRef<HTMLElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navItems = useEndUserDestinations();

  useEffect(() => {
    const element = navbarRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const updateHeight = () => {
      setNavbarHeightPx(element.offsetHeight);
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
      setNavbarHeightPx(0);
    };
  }, [setNavbarHeightPx, features]);

  if (!isLoggedIn) return null;

  const inEndUserArea =
    pathname.startsWith("/end-user") || pathname === "/home";
  // Only the first item matching the path is highlighted.
  const activeIndex = navItems.findIndex(
    (item) => pathname === item.to || pathname.startsWith(item.to + "/"),
  );

  return (
    // Fixed just below the banners the root pins (Jira NAV-227), so the two
    // stay together however far the page scrolls.
    <header
      ref={navbarRef}
      className="fixed z-50 w-full solid-header"
      style={{ top: bannerStackHeightPx }}
    >
      <div className="flex items-center justify-between px-5 py-4 md:px-8">
        <div className="flex items-center gap-6">
          <Link to="/home" className="flex items-center gap-2.5 cursor-pointer">
            <div
              className="w-9 h-9 flex items-center justify-center"
              data-testid="logo-mark"
            >
              <img
                src={hbkLogo}
                alt="Holtzbrinck"
                className="w-9 h-9 object-contain"
              />
            </div>
            <span
              className="font-display text-xl font-semibold text-hb-navy-dark dark:text-white"
              data-testid="logo-text"
            >
              {t("nav.navigator")}
            </span>
          </Link>
          {inEndUserArea && (
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item, index) => {
                const isActive = index === activeIndex;
                const Icon = item.navIcon;
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    search={item.search}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-hb-navy/10 text-hb-navy-dark dark:bg-white/10 dark:text-white"
                        : "text-hb-navy-light dark:text-white/70 hover:bg-hb-navy/5 dark:hover:bg-white/5 hover:text-hb-navy dark:hover:text-white"
                    }`}
                    data-testid={`nav-${item.key}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{t(item.labelKey)}</span>
                    {item.badge ? (
                      <WaitingBadge
                        count={item.badge}
                        testId={`nav-${item.key}-badge`}
                      />
                    ) : null}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* Withdrawn until the help page's wording is agreed (NAV-114) — see
              `helpAvailability`. The route stays reachable by URL so the draft
              can be read; what is gone is the app *offering* it. */}
          {HELP_PAGE_OFFERED && (
            <Link
              to="/help"
              className="flex items-center gap-2 text-sm text-hb-navy-light dark:text-white/70 hover:text-hb-navy dark:hover:text-white transition-colors"
              data-testid="button-help"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">{t("common:needHelp")}</span>
            </Link>
          )}
          <LanguageSwitcher />
          <NotificationBell />
          <UserDropdown />
        </div>
      </div>
      {features.infoBannerText && (
        <div className="py-[0.5rem] px-[0.25rem] bg-primary text-white text-center text-sm content-around whitespace-pre-line">
          <p>{features.infoBannerText}</p>
        </div>
      )}
    </header>
  );
}
